"use client"

import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { editUserInfo } from "../../services/usersApi"
import { getUserData, saveUserData } from "../../utils/auth"
import { useAuth } from "../../utils/AuthContext"
import { toast } from "react-toastify"
import "../../styles/global.css"
import "./EditProfile.css"
import Spinner from "../Spinner/Spinner"
import GeolocationField from "../GeolocationField/GeolocationField"
import { useLocations } from "../../utils/LocationContext"

const EditProfile = () => {
  const locationHook = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const { userInfo } = locationHook.state || {}
  const { setUserData } = useAuth()

  const [formData, setFormData] = useState({
    name: userInfo?.name || "",
    location: userInfo?.location || "",
    locationCoordinates: null,
    birthDate: userInfo?.birthDate || "",
    email: userInfo?.email || "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { detectLocationFromGPS, generateSmartLocation, SmartLocationService } = useLocations() // 🆕 Usar servicio centralizado

  // Capturar SmartLocation enviado por GeolocationField
  // const handleSmartLocationChange = smartLoc => {
  //   setFormData(prev => ({
  //     ...prev,
  //     smartLocation: smartLoc,
  //     location: smartLoc.displayName,
  //     locationCoordinates: smartLoc.coordinates
  //         ? { latitude: smartLoc.coordinates.lat, longitude: smartLoc.coordinates.lng, accuracy: smartLoc.isApproximate }
  //         : null
  //   }))
  // }


  const handleChange = (e) => {
    const { name, value } = e.target
    // 🔧 USAR prevFormData para evitar race conditions
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }))
  }

  // 🆕 Función modificada para manejar cambios de ubicación con detección automática
  const handleLocationChange = async (locationName, coordinates = null, isSystemLocation = false) => {

    // Si hay coordenadas GPS, usar el servicio centralizado para detectar
    if (coordinates && !isSystemLocation) {
      try {
        const detectionResult = await detectLocationFromGPS(coordinates.latitude, coordinates.longitude)
        if (detectionResult.success) {
          setFormData(prevFormData => ({
            ...prevFormData,
            location: detectionResult.displayName,
            locationCoordinates: coordinates // ✅ Preservar coordenadas GPS
          }))
          return
        }
      } catch (error) {
        console.log('Error en detección automática, usando valor original')
      }
    }

    // ⚠️ IMPORTANTE: Si ya hay coordenadas GPS en formData, NO las sobrescribas con null
    // Usar setFormData con callback para acceder al valor actualizado de formData
    setFormData(prevFormData => {

      // Preservar coordenadas existentes si no hay nuevas coordenadas
      const finalCoordinates = coordinates || prevFormData.locationCoordinates

      return {
        ...prevFormData,
        location: locationName,
        locationCoordinates: finalCoordinates // ✅ Preservar coordenadas existentes si no hay nuevas
      }
    })
  }

  const handleCoordinatesChange = (coordinates) => {
    setFormData(prevFormData => ({ ...prevFormData, locationCoordinates: coordinates }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Preparar payload con datos actualizados para editar perfil
    const { name, birthDate, email, locationCoordinates } = formData
    let smartLocation = null

    if (locationCoordinates) {
      // generar smartLocation usando servicio centralizado si hay coordenadas
      const detectionResult = await detectLocationFromGPS(locationCoordinates.latitude, locationCoordinates.longitude)
      if (detectionResult.success) {
        smartLocation = generateSmartLocation(detectionResult, locationCoordinates)
      }
    }

    // Si no se generó smartLocation vía GPS, usar selección manual o texto ingresado
    if (!smartLocation) {
      smartLocation = SmartLocationService.generateManualSmartLocation(formData.location)
    }

    const payload = { name, email, birthDate, smartLocation }


    setLoading(true)
    setIsSubmitting(true)

    try {
      // Enviar payload completo al backend para editar perfil
      await editUserInfo(payload)
      // Actualizar cache y contexto con TODOS los datos incluyendo email y smartLocation completo
      const updatedUserData = { ...getUserData(), name, email, birthDate, smartLocation }
      saveUserData(updatedUserData)
      setUserData(updatedUserData)
      toast.success("Perfil actualizado correctamente")
      navigate("/usuario/perfil")
    } catch (error) {
      console.error("❌ EditProfile - ERROR al actualizar perfil:", error)
      toast.error("Error al actualizar el perfil")
    } finally {
      setIsSubmitting(false)
      setLoading(false)
    }
  }

  return (
      <div className="user-profile-container profile-layout">
        <main className="profile-content">
          <div className="profile-section">
            <div className="profile-card">
              <div className="profile-card-header">
                <h2 className="profile-heading">Editar Perfil</h2>
                {loading && (
                    <div className="spinner-container">
                      <Spinner />
                    </div>
                )}
              </div>

              <div className="profile-card-body">
                <form onSubmit={handleSubmit} className="edit-profile-form">
                  <div className="form-field-container">
                    <div className="form-field">
                      <label htmlFor="name" className="form-label">Nombre</label>
                      <input
                          id="name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-control"
                          required
                      />
                    </div>
                  </div>

                  <div className="form-field-container">
                    <div className="form-field">
                      <label htmlFor="location" className="form-label">Ubicación</label>
                      <GeolocationField
                          value={formData.location}
                          onChange={handleLocationChange}
                          onCoordinatesChange={handleCoordinatesChange}
                          label="Ubicación"
                          required={true}
                          allowCustom={true}
                          showCoordinatesOption={false}
                      />
                    </div>
                  </div>

                  <div className="form-field-container">
                    <div className="form-field">
                      <label htmlFor="birthDate" className="form-label">Fecha de Nacimiento</label>
                      <input
                          id="birthDate"
                          type="date"
                          name="birthDate"
                          value={formData.birthDate}
                          onChange={handleChange}
                          className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-field-container">
                    <div className="form-field">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input
                          id="email"
                          type="email"
                          value={userInfo?.email || ''}
                          disabled
                          className="form-control"
                      />
                    </div>
                  </div>

                  <div className="profile-actions-row">
                    <button
                        type="button"
                        className="new-btn btn-muted"
                        onClick={() => navigate('/usuario/perfil')}
                        disabled={isSubmitting}
                    >
                      Volver
                    </button>
                    <button
                        type="submit"
                        className="new-btn btn-accent"
                        disabled={isSubmitting}
                    >
                      {isSubmitting ? "Guardando..." : "Guardar Cambios"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
  )
}

export default EditProfile